import { CDataTable } from "@coreui/react";
import { useEffect, useState } from "react";
import {
  BasicSelect,
  DisplayRichText,
  NestedTablesContainer,
  SaveButton,
} from "src/components";
import Checkbox from "src/components/Checkbox/Checkbox";
import DisplayFiles from "src/components/DisplayFiles";
import { DB_PATHS } from "src/constants";
import { useBasicForm, useUpdateDatabase } from "src/hooks";
import { convertStrToBool } from "src/utils/common-utils";
import { ESG_INTERNAL_COLUMNS } from "../../constants";
import { useEsgContext } from "../../context";
import useEsgEmails from "../../hooks/useEsgEmails";
import { checkQuestionIsSelect } from "../../utils";
import { createEsgInternalRows } from "../../utils/create-rows";
import AdminComment from "./AdminComment";

const SelfSurvey = () => {
  let rows = [];

  const [sendEmail, setSendEmail] = useState(false);

  const {
    internalSurveyResponses,
    adminSurveyResponses,
    currentSurvey,
    orgId,
    periodId,
    isStandalone,
  } = useEsgContext();

  const surveyAreas = currentSurvey?.areas || [];

  if (internalSurveyResponses) {
    rows = createEsgInternalRows({
      orgId,
      internalSurveyResponses,
      adminSurveyResponses,
      surveyAreas,
      isStandalone,
    });
  }

  const { setOrUpdateDocument } = useUpdateDatabase();

  let path = [];

  if (isStandalone) {
    path = ["esg-survey", orgId, "entries", "admin"];
  } else {
    path = [
      ...DB_PATHS.assessmentSurveyEntries
        .join("/")
        .replace("ORG_ID", orgId)
        .replace("PERIOD_ID", periodId)
        .split("/"),
      "admin",
    ];
  }
  const writeToDB = async (values) => {
    await setOrUpdateDocument(path, {
      ...values,
      isVerified: true,
      isFinal: isFinal,
      id: "admin",
    });
  };

  const {
    onFormFieldChange: onChange,
    onFormSubmit,
    formMethods: { control },
  } = useBasicForm({
    writeToDB,
  });

  const { sendEsgEmail } = useEsgEmails();

  const { sendEmails: userWantsEmails } = internalSurveyResponses;

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isFinal, setIsFinal] = useState(false);

  useEffect(() => {
    setIsFinal(adminSurveyResponses?.isFinal || false);
  }, [adminSurveyResponses?.isFinal]);

  const onSubmit = async () => {
    setIsSubmitting(true);
    try {
      onFormSubmit();
      if (sendEmail && userWantsEmails) {
        const { email, name, orgName } = internalSurveyResponses;

        await sendEsgEmail({
          respondentId: orgId,
          orgName,
          firstName: name.split(" ")[0],
          email,
          type: "admin",
        });
      }
    } catch (e) {
      console.log(e);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="my-3">
        <NestedTablesContainer>
          <CDataTable
            fields={ESG_INTERNAL_COLUMNS}
            items={rows}
            addTableClasses={"upper"}
            scopedSlots={{
              question: (item, index) => {
                return (
                  <td>
                    <DisplayRichText value={item.question} />
                  </td>
                );
              },
              "self-score": (q, index) => {
                return (
                  <td>
                    {q.type === "select" ? (
                      <DisplayRichText value={q["self-score"]} />
                    ) : q.type === "text" ? (
                      <DisplayRichText value={q.answer} />
                    ) : q.type === "number" ||
                      q.type === "date" ||
                      q.type === "date-range" ? (
                      <span>{q.answer}</span>
                    ) : null}
                  </td>
                );
              },
              details: (item) => {
                const { evidence, storagePath } = item;

                return (
                  <div className=" px-3">
                    <div>
                      {!!evidence && (
                        <div>
                          <p className="p-0 mb-1">
                            <b>Explanation:</b> {evidence}
                          </p>
                        </div>
                      )}
                      <DisplayFiles storagePath={storagePath} />
                    </div>
                    <AdminComment
                      item={item}
                      control={control}
                      onChange={onChange}
                    />
                  </div>
                );
              },
              "admin-score": (item) => {
                if (item.question === "Total")
                  return <td>{item["admin-score"]}</td>;

                if (item.weight !== undefined && +item.weight === 0)
                  return <td>Unscored</td>;
                const { area, key } = item;

                const answers =
                  surveyAreas
                    ?.find((step) => step.key === area)
                    ?.questions?.find((q) => q.key === key)?.answers || [];

                const options = answers.map((a) => ({
                  value: a.value,
                  label: a.value,
                }));

                const fullOptions = [...options];

                if (checkQuestionIsSelect(item)) {
                  fullOptions.unshift({
                    label: "Agree with them",
                    value: "n/a",
                  });
                }

                return (
                  <td style={{ minWidth: "200px" }}>
                    {area && (
                      <BasicSelect
                        defaultValue={item["admin-score"]}
                        options={fullOptions}
                        showNone={false}
                        name={`${area}.${key}.score`}
                        onChange={onChange}
                      />
                    )}
                  </td>
                );
              },
            }}
          />
        </NestedTablesContainer>
      </div>
      <Checkbox
        className="mt-4 mb-2"
        name="sendEmails"
        id="sendEmails"
        label="Send notification email"
        disabled={!userWantsEmails}
        onChange={(e) => {
          const val = convertStrToBool(e.target.checked);
          setSendEmail(val);
        }}
        value={sendEmail}
      />
      <Checkbox
        className="mt-4 mb-2"
        style={{ fontSize: "18px" }}
        name="isFinal"
        id="isFinal"
        label="Finalize the survey"
        onChange={(e) => {
          const val = convertStrToBool(e.target.checked);
          setIsFinal(val);
          onChange({ target: { name: "isFinal", value: val } });
        }}
        value={isFinal}
      />
      <SaveButton loading={isSubmitting} onClick={onSubmit} />
    </>
  );
};

export default SelfSurvey;
